import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import { useAppDispatch } from '../../../store';
import {
  fetchSuggestedTasks,
  updateSuggestedTasks,
} from '../../../store/actions/Matters/MatterActions';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { fetchAllTasks, createBulkTasks, completeTask, archiveTask, updateTask, addTaskComment } from '../../../store/actions/TasksActions';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import { showModal, hideModal } from '../../../store/actions/modalActions';
import Button from '../../shared/TailwindComponents/Button';
import FilterBarWithTabs from '../../shared/TailwindComponents/FilterBarWithTabs';
import Table from '../../shared/TailwindComponents/Table';
import SkeletonClientItem from '../../Clients/SkeletonClientItem';
import Input from '../../shared/TailwindComponents/Input';
import Textarea from '../../shared/TailwindComponents/Textarea';
import Badge from '../../shared/TailwindComponents/Badge';
import Dropdown from '../../shared/TailwindComponents/Dropdown';
import DateTimePicker from '../../shared/TailwindComponents/DatePicker';
import ProfileImage from '../../Header/ProfileImage';
import { BadgeColor } from '../../shared/TailwindComponents/types/badgeTypes';
import { formatStatus } from '../../../utils/functions';
import dayjs from 'dayjs';
import {
  PiCheckSquareLight,
  PiLightbulbLight,
  PiTrashLight,
  PiPlusLight,
  PiCheckCircleLight,
  PiClockClockwiseLight,
  PiFlagLight,
  PiFlagFill,
  PiPaperclipLight,
  PiChatCircleTextLight,
  PiArchiveLight,
  PiSparkleLight,
} from 'react-icons/pi';
import { CheckIcon } from '@heroicons/react/24/solid';
import { motion } from 'framer-motion';
import { MatterData } from '../MatterDetails';
import clsx from 'clsx';
import { useGenerateMore } from '../../../hooks/useGenerateMore';
import FindMoreProgress from '../FindMoreProgress';
import SubTabBar from '../../shared/TailwindComponents/SubTabBar';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, tasksItemsTourSteps } from '../../shared/FeatureTour';

export interface SuggestedTaskItem {
  TaskTitle: string;
  TaskDescription: string;
  TaskDeadline: string;
  Priority?: string;
  MatterID?: string;
  CaseID?: string;
  selected?: boolean;
}

interface TasksTabContentProps {
  matterData: MatterData | null;
  suggestedTasks: SuggestedTaskItem[];
  setSuggestedTasks: React.Dispatch<React.SetStateAction<SuggestedTaskItem[]>>;
  matterTasks: any[];
  setMatterTasks: React.Dispatch<React.SetStateAction<any[]>>;
  suggestedTasksLoading: boolean;
  matterTasksLoading: boolean;
  userAccessLevel: 'edit' | 'admin' | 'view';
  onUpdate: () => void;
  hasDocuments?: boolean;
}

type TasksSubTab = 'suggested' | 'active';

const TasksTabContent: React.FC<TasksTabContentProps> = ({
  matterData,
  suggestedTasks,
  setSuggestedTasks,
  matterTasks,
  setMatterTasks,
  suggestedTasksLoading,
  matterTasksLoading,
  userAccessLevel,
  onUpdate,
  hasDocuments = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { matterId } = useParams<{ matterId: string }>();
  const canEdit = userAccessLevel === 'edit' || userAccessLevel === 'admin';

  const matterUuid = matterData?.uuid || matterData?.MatterID || matterId || '';

  const [subTab, setSubTab] = useState<TasksSubTab>('suggested');
  const [searchTerm, setSearchTerm] = useState('');
  const [isTabLoading, setIsTabLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const userInfo = useSelector((state: RootState) => state.user.userInfo);

  const { isGenerating: isFindingMoreTasks, handleGenerateMore: handleFindMoreTasks, progress: findMoreProgress } = useGenerateMore({
    matterUuid,
    targetStep: 'tasks',
    onComplete: onUpdate,
  });

  const {
    isRunning: isItemsTourRunning,
    handleJoyrideCallback: handleItemsTourCallback,
  } = useFeatureTour({
    tourId: 'tasks-items',
    steps: tasksItemsTourSteps,
    readyToShow: (suggestedTasks.length > 0 || matterTasks.length > 0) && !suggestedTasksLoading && !matterTasksLoading,
    autoStartDelay: 600,
  });

  // Editable local copy of suggested tasks
  const [editableTasks, setEditableTasks] = useState<SuggestedTaskItem[]>([]);

  useEffect(() => {
    setEditableTasks(
      suggestedTasks.map((task) => ({ ...task, selected: true }))
    );
  }, [suggestedTasks]);

  const refreshSuggestedTasks = useCallback(async () => {
    if (!matterUuid) return;
    setIsTabLoading(true);
    try {
      const tasks = await dispatch(fetchSuggestedTasks(matterUuid));
      setSuggestedTasks(tasks || []);
    } catch (error) {
      console.error('Failed to load suggested tasks:', error);
    } finally {
      setIsTabLoading(false);
    }
  }, [matterUuid, dispatch, setSuggestedTasks]);

  const refreshMatterTasks = useCallback(async () => {
    if (!matterUuid) return;
    setIsTabLoading(true);
    try {
      const result = await dispatch(
        fetchAllTasks({ matter_uuid: matterUuid, limit: 100 })
      );
      setMatterTasks(result?.tasks || []);
    } catch (error) {
      console.error('Failed to load matter tasks:', error);
    } finally {
      setIsTabLoading(false);
    }
  }, [matterUuid, dispatch, setMatterTasks]);

  // --- Sub-tab definitions (matches Sources/Q&As pattern) ---

  const taskTabs = useMemo(() => [
    {
      id: 'suggested' as TasksSubTab,
      label: t('Suggestions'),
      icon: <PiLightbulbLight className="h-3 w-3" />,
      count: suggestedTasks.length,
      color: 'yellow' as BadgeColor,
    },
    {
      id: 'active' as TasksSubTab,
      label: t('Active Tasks'),
      icon: <PiCheckSquareLight className="h-3 w-3" />,
      count: matterTasks.length,
      color: 'blue' as BadgeColor,
    },
  ], [t, suggestedTasks.length, matterTasks.length]);

  // --- Suggested Tasks handlers ---

  const handleEditTask = (index: number, field: keyof SuggestedTaskItem, value: string | boolean) => {
    setEditableTasks((prev) => {
      const updated = [...prev];
      if (field === 'selected') {
        updated[index] = { ...updated[index], selected: value as boolean };
      } else {
        updated[index] = { ...updated[index], [field]: value as string };
      }
      return updated;
    });
  };

  const toggleSelect = (index: number) => {
    handleEditTask(index, 'selected', !editableTasks[index].selected);
  };

  const handleRemoveSuggested = async (index: number) => {
    if (!canEdit) return;
    const updated = editableTasks.filter((_, i) => i !== index);
    setEditableTasks(updated);

    const tasksToSave = updated.map(({ selected, ...rest }) => rest);
    await dispatch(updateSuggestedTasks(matterUuid, tasksToSave));
    setSuggestedTasks(tasksToSave);
    dispatch(
      showFeedbackModal({
        modalType: 'success',
        message: t('Suggested task removed'),
        showModal: true,
        duration: 3000,
      })
    );
  };

  const handleAddSuggestedTask = () => {
    setEditableTasks((prev) => [
      ...prev,
      {
        TaskTitle: '',
        TaskDescription: '',
        TaskDeadline: '',
        Priority: 'medium',
        MatterID: matterUuid,
        selected: true,
      },
    ]);
  };

  const handleGenerateSelected = async () => {
    const selected = editableTasks.filter((t) => t.selected);
    if (selected.length === 0) {
      dispatch(
        showFeedbackModal({
          modalType: 'warning',
          message: t('Please select at least one task to generate'),
          showModal: true,
          duration: 3000,
        })
      );
      return;
    }

    setIsGenerating(true);
    try {
      const tasksToCreate = selected.map(({ selected: _, ...rest }) => ({
        ...rest,
        MatterID: rest.MatterID || matterUuid,
      }));

      const result = await dispatch(createBulkTasks(tasksToCreate, matterUuid));

      if (result && result.created_count > 0) {
        const remaining = editableTasks.filter((t) => !t.selected);
        const remainingToSave = remaining.map(({ selected, ...rest }) => rest);
        await dispatch(updateSuggestedTasks(matterUuid, remainingToSave));
        setSuggestedTasks(remainingToSave);

        await refreshMatterTasks();
        onUpdate();
      }
    } catch (error) {
      console.error('Failed to generate tasks:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  // --- Active Tasks action handlers ---

  const handleCompleteTask = async (taskUuid: string) => {
    dispatch(
      showModal({
        type: 'addTaskComment',
        message: t('Complete Task'),
        icon: <PiCheckCircleLight className="w-6 h-6 text-green-600" />,
        subMsg: t('Add an optional comment to let others know about the completion.'),
        onConfirm: async (comment: string) => {
          try {
            await dispatch(completeTask(taskUuid) as any);
            if (comment?.trim()) {
              await dispatch(addTaskComment(taskUuid, comment) as any);
            }
            await refreshMatterTasks();
            onUpdate();
            dispatch(hideModal());
          } catch (error) {
            dispatch(showFeedbackModal({ modalType: 'error', message: t('Error completing task.'), showModal: true, duration: 3000 }));
          }
        },
        showModal: true,
        onCancel: () => dispatch(hideModal()),
      } as any)
    );
  };

  const handleArchiveTask = async (taskUuid: string) => {
    dispatch(
      showModal({
        showModal: true,
        type: 'confirmation',
        message: t('Archive Task'),
        subMsg: t('This task will be archived. You can restore it later if needed.'),
        icon: <PiArchiveLight className="w-6 h-6 text-red-600" />,
        onConfirm: async () => {
          try {
            await dispatch(archiveTask(taskUuid) as any);
            await refreshMatterTasks();
            onUpdate();
            dispatch(hideModal());
          } catch (error) {
            dispatch(showFeedbackModal({ modalType: 'error', message: t('Error archiving task.'), showModal: true, duration: 3000 }));
            dispatch(hideModal());
          }
        },
      })
    );
  };

  const handleRestoreTask = async (taskUuid: string) => {
    try {
      await dispatch(updateTask({ TaskID: taskUuid, Status: 'todo' }) as any);
      dispatch(showFeedbackModal({ modalType: 'success', message: t('Task restored.'), showModal: true, duration: 3000 }));
      await refreshMatterTasks();
      onUpdate();
    } catch (error) {
      dispatch(showFeedbackModal({ modalType: 'error', message: t('Error restoring task.'), showModal: true, duration: 3000 }));
    }
  };

  // --- Active Tasks helpers ---

  const getStatusBadgeColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'green';
      case 'in_progress':
      case 'in progress': return 'blue';
      case 'pending': return 'yellow';
      case 'overdue': return 'red';
      default: return 'gray';
    }
  };

  const getPriorityBadgeColor = (priority: string): BadgeColor => {
    switch (priority?.toLowerCase()) {
      case 'critical': return 'red';
      case 'high': return 'red';
      case 'medium': return 'yellow';
      case 'low': return 'green';
      default: return 'gray';
    }
  };

  const getPriorityColor = (priority?: string): string => {
    const p = priority?.toLowerCase() || '';
    if (p === 'critical') return 'red';
    if (p === 'high') return 'orange';
    if (p === 'medium') return 'yellow';
    if (p === 'low') return 'green';
    return 'gray';
  };

  // --- Filtering ---

  const filteredSuggestedTasks = useMemo(() =>
    editableTasks.filter(
      (task) =>
        !searchTerm ||
        task.TaskTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.TaskDescription?.toLowerCase().includes(searchTerm.toLowerCase())
    ),
    [editableTasks, searchTerm]
  );

  const filteredMatterTasks = useMemo(() =>
    matterTasks.filter(
      (task) =>
        !searchTerm ||
        task.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.description?.toLowerCase().includes(searchTerm.toLowerCase())
    ),
    [matterTasks, searchTerm]
  );

  // --- Priority options (matches TaskForm) ---

  const priorityOptions = useMemo(() => [
    { value: 'low', label: t('Low') },
    { value: 'medium', label: t('Medium') },
    { value: 'high', label: t('High') },
    { value: 'critical', label: t('Critical') },
  ], [t]);

  // --- Loading states (content only, tab bar stays visible) ---

  const isContentLoading =
    (subTab === 'suggested' && (suggestedTasksLoading || isTabLoading)) ||
    (subTab === 'active' && (matterTasksLoading || isTabLoading));

  const selectedCount = editableTasks.filter((t) => t.selected).length;

  // --- Render content skeleton (inside layout, tab bar stays) ---

  const renderContentSkeleton = () => (
    <div className="py-2">
      <SkeletonClientItem viewMode="table" count={5} />
    </div>
  );

  // --- Render suggested tasks content ---

  const renderSuggestedContent = () => {
    if (editableTasks.length === 0) {
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-center justify-center py-16 text-center"
        >
          <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
            <PiLightbulbLight className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {t('No Suggested Tasks')}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
            {t('Generated task suggestions will appear here once the matter has been processed.')}
          </p>
          {canEdit && (
            <div className="flex items-center gap-2 mt-4">
              <Button
                variant="outline"
                size="small"
                onClick={handleAddSuggestedTask}
              >
                <PiPlusLight className="h-4 w-4 mr-1" />
                {t('Add Task Manually')}
              </Button>
              <Button
                variant="primary"
                size="small"
                onClick={handleFindMoreTasks}
                disabled={!hasDocuments}
                tooltip={!hasDocuments ? t('Upload at least one case file to enable AI generation.') : ''}
                icon={<PiSparkleLight className="h-3.5 w-3.5" />}
              >
                {t('Generate')}
              </Button>
            </div>
          )}
        </motion.div>
      );
    }

    if (filteredSuggestedTasks.length === 0) {
      return (
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
          {t('No suggestions match your search.')}
        </p>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredSuggestedTasks.map((task, index) => {
          const originalIndex = editableTasks.indexOf(task);
          return (
            <motion.div
              key={originalIndex}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className={clsx(
                'relative bg-white dark:bg-gray-800 border rounded-xl shadow-sm transition-all duration-300 hover:shadow-md overflow-hidden',
                task.selected
                  ? 'border-primary-300 dark:border-primary-600'
                  : 'border-gray-200 dark:border-gray-700 opacity-60 hover:opacity-80'
              )}
            >
              {/* Top bar: checkbox + priority badge + delete */}
              <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-gray-100 dark:border-gray-700/50">
                <div className="flex items-center gap-3">
                  {canEdit && (
                    <input
                      type="checkbox"
                      checked={task.selected}
                      onChange={() => toggleSelect(originalIndex)}
                      className="form-checkbox h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
                    />
                  )}
                  <Badge color={getPriorityBadgeColor(task.Priority || 'medium')}>
                    <PiFlagLight className="h-3 w-3 mr-1" />
                    {(task.Priority || 'medium').charAt(0).toUpperCase() + (task.Priority || 'medium').slice(1)}
                  </Badge>
                </div>
                {canEdit && (
                  <button
                    onClick={() => handleRemoveSuggested(originalIndex)}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all rounded-lg"
                    title={t('Remove suggestion')}
                  >
                    <PiTrashLight className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Body */}
              <div className="p-4 flex flex-col gap-3">
                {/* Title */}
                <Input
                  type="text"
                  value={task.TaskTitle}
                  onChange={(e) => handleEditTask(originalIndex, 'TaskTitle', e.target.value)}
                  placeholder={t('Enter task title')}
                  className="text-sm font-semibold text-gray-900 dark:text-white border-none p-0 focus:ring-0 bg-transparent"
                  disabled={!canEdit}
                />

                {/* Description */}
                <Textarea
                  id={`suggested-desc-${originalIndex}`}
                  name={`suggested-desc-${originalIndex}`}
                  value={task.TaskDescription}
                  onChange={(e) => handleEditTask(originalIndex, 'TaskDescription', e.target.value)}
                  placeholder={t('Add task description...')}
                  className="text-sm text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900/50"
                  rows={2}
                  label=""
                  disabled={!canEdit}
                />

                {/* Bottom row: Priority + Due Date */}
                <div className="flex items-center gap-3 pt-1">
                  <div className="flex-1">
                    <Dropdown
                      id={`priority-${originalIndex}`}
                      label=""
                      options={priorityOptions}
                      value={task.Priority || 'medium'}
                      onChange={(val) => handleEditTask(originalIndex, 'Priority', val as string)}
                      placeholder={t('Priority')}
                      compact={true}
                      leftIcon={<PiFlagLight className="h-3.5 w-3.5" />}
                      fullWidth={true}
                    />
                  </div>
                  <div className="flex-1">
                    <DateTimePicker
                      id={`deadline-${originalIndex}`}
                      selected={task.TaskDeadline ? new Date(task.TaskDeadline) : null}
                      onChange={(date: Date | null) =>
                        handleEditTask(originalIndex, 'TaskDeadline', date ? date.toISOString() : '')
                      }
                      placeholder={t('Due date')}
                      showTimeSelect={false}
                      dateFormat="yyyy-MM-dd"
                      disabled={!canEdit}
                      fullWidth={true}
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    );
  };

  // --- Active tasks table data (matches TaskDeadlineTracker pattern) ---

  const activeTableColumns = ['Task Name', 'Status', 'Due Date', 'Priority', 'Access', 'Attachments', 'Comments', 'Actions'];

  const activeTableData = useMemo(() =>
    filteredMatterTasks.map((task) => {
      const collaborators = task.collaborators || task.Collaborators || [];
      const taskStatus = task.status || task.Status || 'todo';
      const dueDate = task.due_date || task.TaskDeadline || task.DueDate || '';
      const priority = task.priority || task.Priority || 'medium';
      const attachmentCount = typeof task.attachment_count === 'number' ? task.attachment_count : (task.Attachments ? task.Attachments.length : 0);
      const commentCount = typeof task.comment_count === 'number' ? task.comment_count : (task.Comments ? task.Comments.length : 0);

      return {
        id: task.id || task.task_id,
        uuid: task.uuid || task.TaskID,
        'Task Name': task.title || task.TaskTitle || task.Title || '',
        Status: (
          <Badge color={getStatusBadgeColor(taskStatus)}>
            {taskStatus === 'deleted' ? t('Archived') : formatStatus(taskStatus || 'todo')}
          </Badge>
        ),
        'Due Date': dueDate ? dayjs(dueDate).format('MMM DD, YYYY') : (
          <span className="text-gray-400 dark:text-gray-500 text-sm">—</span>
        ),
        Priority: (
          <div className="flex items-center gap-1">
            <PiFlagFill className={`w-4 h-4 text-${getPriorityColor(priority)}-500`} />
            <span className="text-sm">{formatStatus(priority || 'None')}</span>
          </div>
        ),
        Collaborators: collaborators && collaborators.length > 0 ? (
          <div className="flex -space-x-2">
            {collaborators.slice(0, 3).map((collab: any, idx: number) => {
              const firstName = collab.first_name || collab.FirstName || '';
              const lastName = collab.last_name || collab.LastName || '';
              const fullName = collab.name || `${firstName} ${lastName}`.trim();
              const nameParts = fullName.trim().split(/\s+/);
              const email = collab.email || collab.Email || '';
              const initials = collab.initials || `${nameParts[0]?.[0] || ''}${nameParts[1]?.[0] || ''}`.toUpperCase() || 'U';
              return (
                <div key={idx} className="relative" title={fullName}>
                  <ProfileImage profilePicture={collab.profile_picture || collab.avatar_url || null} email={email} initials={initials} size="w-7 h-7" firstName={firstName} lastName={lastName} />
                </div>
              );
            })}
            {collaborators.length > 3 && (
              <div className="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-medium text-gray-700 dark:text-gray-200">
                +{collaborators.length - 3}
              </div>
            )}
          </div>
        ) : (
          <span className="text-gray-400 dark:text-gray-500 text-sm">—</span>
        ),
        Attachments: (
          <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
            <PiPaperclipLight className="w-4 h-4" />
            <span>{attachmentCount}</span>
          </div>
        ),
        Comments: (
          <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
            <PiChatCircleTextLight className="w-4 h-4" />
            <span>{commentCount}</span>
          </div>
        ),
        Actions: null,
        rawTask: task,
      };
    }),
    [filteredMatterTasks, t]
  );

  const canUserManageTask = (task: any): boolean => {
    if (!task || !userInfo) return false;
    const isCreator = task.created_by_user_id === userInfo.UserID;
    const isAssignee = task.assigned_to_user_id === userInfo.UserID || task.UserID === userInfo.UserID;
    if (isCreator || isAssignee) return true;
    if (canEdit) return true;
    return false;
  };

  const activeTaskActions = useMemo(() => [
    {
      label: t('Complete'),
      onClick: (row: any) => handleCompleteTask(row.uuid || row.id),
      icon: <CheckIcon className="h-4 w-4" />,
      disabled: (row: any) => {
        const status = (row.rawTask?.status || row.rawTask?.Status || 'todo').toLowerCase();
        return !canUserManageTask(row.rawTask) || status === 'completed' || status === 'deleted' || status === 'archived';
      },
    },
    {
      label: t('Archive'),
      onClick: (row: any) => handleArchiveTask(row.uuid || row.id),
      icon: <PiArchiveLight className="h-4 w-4 text-red-600" />,
      isDestructive: true,
      disabled: (row: any) => {
        const status = (row.rawTask?.status || row.rawTask?.Status || 'todo').toLowerCase();
        return !canUserManageTask(row.rawTask) || status === 'deleted' || status === 'archived';
      },
    },
    {
      label: t('Restore'),
      onClick: (row: any) => handleRestoreTask(row.uuid || row.id),
      icon: <PiClockClockwiseLight className="h-4 w-4" />,
      disabled: (row: any) => {
        const status = (row.rawTask?.status || row.rawTask?.Status || 'todo').toLowerCase();
        return !canUserManageTask(row.rawTask) || (status !== 'deleted' && status !== 'archived' && status !== 'completed');
      },
    },
  ], [t, userInfo]);

  if (!matterData) {
    return (
      <div className="p-6 text-center text-gray-500 dark:text-gray-400">
        {t('Loading tasks...')}
      </div>
    );
  }

  // --- Render active tasks content ---

  const renderActiveContent = () => {
    return (
      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700">
        <Table
          columns={activeTableColumns}
          data={activeTableData}
          onRowClick={(row) => {
            const taskId = row.uuid || row.id;
            if (taskId) history.push(`/tasks/${taskId}`);
          }}
          actions={activeTaskActions}
          resizableColumns={true}
          defaultColumnWidths={{
            'Task Name': 260,
            'Status': 130,
            'Due Date': 160,
            'Priority': 130,
            'Access': 140,
            'Attachments': 120,
            'Comments': 120,
            'Actions': 48,
          }}
          emptyState={{
            icon: <PiCheckSquareLight className="h-8 w-8" />,
            headline: searchTerm
              ? t('No matching tasks')
              : t('No Tasks Yet'),
            subtext: searchTerm
              ? t('Try adjusting your search.')
              : t('Tasks linked to this matter will appear here. Generate tasks from suggestions or create them from the Tasks page.'),
          }}
          rowSize="compact"
          max='max-h-[calc(100vh-225px)]'
          min='min-h-[calc(100vh-225px)]'
        />
      </div>
    );
  };

  return (
    <div className="space-y-2">
      <div data-tour-id="tasks-sub-tabs">
      <SubTabBar
        tabs={taskTabs}
        activeTab={subTab}
        onTabChange={(id) => {
          setSubTab(id as TasksSubTab);
          setSearchTerm('');
          if (id === 'active' && matterTasks.length === 0) refreshMatterTasks();
        }}
        actions={canEdit && subTab === 'suggested' && !isFindingMoreTasks ? (
          <>
            <Button
              variant="outline"
              size="small"
              onClick={handleAddSuggestedTask}
            >
              <PiPlusLight className="h-4 w-4 mr-1" />
              {t('Add Task')}
            </Button>
            {editableTasks.length > 0 && (
              <Button
                variant="outline"
                size="small"
                onClick={handleGenerateSelected}
                disabled={selectedCount === 0 || isGenerating}
              >
                {isGenerating ? t('Generating...') : `${t('Generate Selected')} (${selectedCount})`}
              </Button>
            )}
            <Button
              variant="primary"
              size="small"
              onClick={handleFindMoreTasks}
              disabled={!hasDocuments}
              tooltip={!hasDocuments ? t('Upload at least one case file to enable AI generation.') : ''}
              icon={<PiSparkleLight className="h-3.5 w-3.5" />}
            >
              {suggestedTasks.length === 0 ? t('Generate') : t('Find More')}
            </Button>
          </>
        ) : undefined}
      />
      </div>

      {/* Search + content area */}
      {isFindingMoreTasks || findMoreProgress.status === 'complete' || findMoreProgress.status === 'failed' ? (
        <FindMoreProgress progress={findMoreProgress} isGenerating={isFindingMoreTasks} />
      ) : (
      <div>
        <FilterBarWithTabs
          onSearchChange={setSearchTerm}
          noParentPadding={true}
          noParentHeight={true}
          itemCount={subTab === 'suggested' ? filteredSuggestedTasks.length : filteredMatterTasks.length}
          totalItemCount={subTab === 'suggested' ? editableTasks.length : matterTasks.length}
          itemCountLabel={subTab === 'suggested'
            ? (editableTasks.length === 1 ? 'suggestion' : 'suggestions')
            : (matterTasks.length === 1 ? 'task' : 'tasks')
          }
        />

        <div data-tour-id="tasks-content-area" className="p-0 min-h-[380px] max-h-[calc(100vh-220px)] overflow-y-auto overflow-x-hidden custom-scrollbar-main">
          {isContentLoading
            ? renderContentSkeleton()
            : subTab === 'suggested'
              ? renderSuggestedContent()
              : renderActiveContent()
          }
        </div>
      </div>
      )}
      <FeatureTour
        steps={tasksItemsTourSteps}
        run={isItemsTourRunning}
        onCallback={handleItemsTourCallback}
        disableOverlay
      />
    </div>
  );
};

export default TasksTabContent;
